import type { PersistedSession, TokenStorage } from '@echo/core';

const KEY = 'echo.session';

/**
 * Browser session storage: only the refresh token (and id token) are persisted, never
 * the access token — the same rule OnEarly `app` applies on web. localStorage is the
 * skeleton default; the production choice (httpOnly cookie via a BFF, or keeping the
 * refresh token server-side) is tracked in docs/decisions/0003-auth.md.
 */
export class BrowserTokenStorage implements TokenStorage {
  async load(): Promise<PersistedSession | undefined> {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as Partial<PersistedSession>;
      return typeof parsed.refreshToken === 'string'
        ? {
            refreshToken: parsed.refreshToken,
            idToken: parsed.idToken,
            ...(typeof parsed.clientId === 'string' ? { clientId: parsed.clientId } : {}),
          }
        : undefined;
    } catch {
      return undefined;
    }
  }
  async save(session: PersistedSession): Promise<void> {
    window.localStorage.setItem(KEY, JSON.stringify(session));
  }
  async clear(): Promise<void> {
    window.localStorage.removeItem(KEY);
  }
}

/**
 * Ory rotates refresh tokens with reuse detection: two tabs refreshing with the same token
 * revoke the whole family. Serialise refreshes with the Web Locks API where it exists.
 */
export const withBrowserRefreshLock = <T>(work: () => Promise<T>): Promise<T> =>
  typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request('echo-token-refresh', work)
    : work();

/** A `@echo/core` store adapter over one localStorage key; unavailable storage reads as empty. */
function localStorageText(key: string) {
  return {
    load(): string | null {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    save(serialised: string): void {
      try {
        window.localStorage.setItem(key, serialised);
      } catch {
        // Private mode or blocked site data: the value lasts the visit.
      }
    },
  };
}

/** Quick math left after an answer: Home shows it in progress and it resumes where it was. */
export const localStorageExerciseProgress = localStorageText('echo.exerciseProgress');

/** Account › Exercise duration in the browser, until the account's own is read. */
export const localStorageExerciseMinutes = {
  load(): number | null {
    const raw = localStorageText('echo.exerciseMinutes').load();
    const minutes = raw ? Number(raw) : Number.NaN;
    return Number.isInteger(minutes) ? minutes : null;
  },
  save(minutes: number): void {
    localStorageText('echo.exerciseMinutes').save(String(minutes));
  },
  /** A duration chosen while the account could not take it, until it does. */
  loadUnsaved(): number | null {
    const raw = localStorageText('echo.exerciseMinutesUnsaved').load();
    const minutes = raw ? Number(raw) : Number.NaN;
    return Number.isInteger(minutes) ? minutes : null;
  },
  saveUnsaved(minutes: number | null): void {
    if (minutes !== null) {
      localStorageText('echo.exerciseMinutesUnsaved').save(String(minutes));
      return;
    }
    try {
      window.localStorage.removeItem('echo.exerciseMinutesUnsaved');
    } catch {
      // Private mode or blocked site data: nothing was kept.
    }
  },
};

/** Account › Large text: every text level at 150%, kept in the browser too. */
export const localStorageLargeText = {
  load(): boolean | null {
    const raw = localStorageText('echo.largeText').load();
    return raw === null ? null : raw === '1';
  },
  save(enabled: boolean): void {
    localStorageText('echo.largeText').save(enabled ? '1' : '0');
  },
};
